import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Text,
  useToast,
  Spinner,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import { PersonCell } from "../../components/Hrms/hrmsUi";
import { SearchIcon } from "@chakra-ui/icons";
import { hrmsDateBounds, validateProfileDates } from "./hrmsDateRules";

interface EmployeeProfile {
  _id: string;
  name: string;
  email: string;
  designation: string;
  dateOfJoining: string;
  birthday: string;
  address: string;
  reportingManager: string;
  nextAppraisalDueDate: string;
  role: string;
  roleIds?: string[];
  isOnboarded: boolean;
  employeeId?: string;
  department?: string;
  division?: string;
  employmentStatus?: string;
  promotionCount?: number;
  leaveCycleStartDate?: string;
  leaveQuotaOverride?: { casual?: number; sick?: number; exigency?: number } | null;
}

const EMPLOYMENT_STATUSES = [
  { value: "probation", label: "Probation", color: "orange" },
  { value: "confirmed", label: "Confirmed", color: "green" },
  { value: "notice", label: "Notice period", color: "yellow" },
  { value: "exited", label: "Exited", color: "gray" },
];

const QUOTA_TYPES = [
  { key: "casual", label: "Casual" },
  { key: "sick", label: "Sick" },
  { key: "exigency", label: "Exigency" },
] as const;

const emptyQuota = { casual: "", sick: "", exigency: "" };

interface RoleOption {
  _id: string;
  name: string;
}

export const ManageEmployees = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const navigate = useNavigate();
  const { canManageEmployees } = getUserAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [employees, setEmployees] = useState<EmployeeProfile[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeProfile | null>(null);
  const [showExited, setShowExited] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    designation: "",
    dateOfJoining: "",
    birthday: "",
    address: "",
    reportingManager: "",
    nextAppraisalDueDate: "",
    roleIds: [] as string[],
    employeeId: "",
    department: "",
    division: "",
    employmentStatus: "",
    promotionCount: 0,
    leaveCycleStartDate: "",
    // Kept as text so a field can be left blank (= use the standard quota).
    quota: { ...emptyQuota },
  });

  const fetchRoles = async () => {
    try {
      const res = await EmployeeDataService.getRoles(token);
      if (res.data.status === "success") {
        setRoles(res.data.data || []);
      }
    } catch (e: any) {
      console.error("Error fetching roles:", e);
    }
  };

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getAllEmployeeProfiles(
        token,
        userEmail
      );
      if (res.data.status === "success") {
        setEmployees(res.data.data || []);
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        toast({
          position: "top",
          title: "Authentication Failed.",
          description: "Your session is expired.",
          status: "error",
          duration: 500,
          isClosable: true,
        });
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      toast({
        position: "top",
        title: "Error",
        description: "Failed to load employees.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (emp: EmployeeProfile) => {
    setSelectedEmployee(emp);
    setEditData({
      name: emp.name || "",
      designation: emp.designation || "",
      dateOfJoining: emp.dateOfJoining || "",
      birthday: emp.birthday || "",
      address: emp.address || "",
      reportingManager: emp.reportingManager || "",
      nextAppraisalDueDate: emp.nextAppraisalDueDate || "",
      roleIds: emp.roleIds || [],
      employeeId: emp.employeeId || "",
      department: emp.department || "",
      division: emp.division || "",
      employmentStatus: emp.employmentStatus || "",
      promotionCount: emp.promotionCount || 0,
      leaveCycleStartDate: emp.leaveCycleStartDate || "",
      quota: {
        casual: String(emp.leaveQuotaOverride?.casual ?? ""),
        sick: String(emp.leaveQuotaOverride?.sick ?? ""),
        exigency: String(emp.leaveQuotaOverride?.exigency ?? ""),
      },
    });
    onOpen();
  };

  const handleSave = async () => {
    if (!selectedEmployee) return;
    const dateError = validateProfileDates(editData);
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    // Blank quota fields mean "standard quota"; send only the ones filled in.
    const { quota, ...fields } = editData;
    const override: Record<string, number> = {};
    for (const { key } of QUOTA_TYPES) {
      if (quota[key].trim() !== "" && !isNaN(Number(quota[key]))) {
        override[key] = Number(quota[key]);
      }
    }
    const payload = {
      ...fields,
      leaveQuotaOverride: Object.keys(override).length ? override : null,
    };

    setIsSaving(true);
    try {
      const res = await EmployeeDataService.updateAdminFields(
        token,
        selectedEmployee.email,
        payload,
        userEmail
      );

      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Success",
          description: `Profile of ${selectedEmployee.name || selectedEmployee.email} updated.`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        fetchEmployees();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description:
          e.response?.data?.message || "Failed to update employee profile.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, []);

  // Reporting manager is stored as an email; show the manager's name.
  const managerName = (value: string) =>
    employees.find((e) => e.email === value)?.name || value;

  const [search, setSearch] = useState("");
  const bounds = hrmsDateBounds();
  const [statusFilter, setStatusFilter] = useState("all");
  const exitedCount = employees.filter((e) => e.employmentStatus === "exited").length;
  const activeEmployees = showExited
    ? employees
    : employees.filter((e) => e.employmentStatus !== "exited");
  const query = search.trim().toLowerCase();
  const visibleEmployees = activeEmployees.filter(
    (e) =>
      (statusFilter === "all" || e.employmentStatus === statusFilter) &&
      (!query ||
        [e.name, e.email, e.employeeId, e.department, e.designation]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(query))),
  );
  const statusChips = [
    { key: "all", label: "All", count: activeEmployees.length },
    ...EMPLOYMENT_STATUSES.filter((st) => showExited || st.value !== "exited")
      .map((st) => ({
        key: st.value,
        label: st.label,
        count: activeEmployees.filter((e) => e.employmentStatus === st.value).length,
      }))
      .filter((chip) => chip.count > 0),
  ];

  const managerOptions = employees.filter(
    (e) => e.employmentStatus !== "exited" && e.email !== selectedEmployee?.email
  );
  // A manager saved before this was a dropdown may be a plain name; keep it selectable.
  const hasLegacyManager =
    !!editData.reportingManager &&
    !managerOptions.some((e) => e.email === editData.reportingManager);

  return (
    <HrmsPageShell
      title="Manage Employees"
      description={`${activeEmployees.length} employees · edit profiles, roles and reporting managers`}
      sectionKey="configuration"
      noPermission={!canManageEmployees}
      isLoading={canManageEmployees && isLoading}
      actions={
        exitedCount > 0 ? (
          <Checkbox
            isChecked={showExited}
            onChange={(e) => setShowExited(e.target.checked)}
          >
            Show exited ({exitedCount})
          </Checkbox>
        ) : undefined
      }
    >
      <>
        <Flex justify="space-between" align="center" gap={3} mb={3} wrap="wrap">
          <Flex gap={2} wrap="wrap">
            {statusChips.map((chip) => {
              const active = statusFilter === chip.key;
              return (
                <Button
                  key={chip.key}
                  size="sm"
                  borderRadius="full"
                  variant="outline"
                  bg={active ? "#327171" : "white"}
                  color={active ? "white" : "gray.700"}
                  borderColor={active ? "#327171" : "gray.200"}
                  _hover={{ borderColor: "#327171" }}
                  onClick={() => setStatusFilter(chip.key)}
                >
                  {chip.label}
                  <Text as="span" ml={1.5} opacity={0.75} fontWeight="500">
                    {chip.count}
                  </Text>
                </Button>
              );
            })}
          </Flex>
          <InputGroup size="sm" maxW={{ base: "100%", md: "300px" }}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              bg="white"
              borderRadius="8px"
              placeholder="Search name, email, ID, department"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </Flex>
        <HrmsTable
          head={[
            "Employee",
            "ID",
            "Department",
            "Reporting Manager",
            "Status",
            "Onboarding",
            "",
          ]}
          isEmpty={visibleEmployees.length === 0}
          emptyText="No employees found"
        >
          {visibleEmployees.map((emp) => {
            const status = EMPLOYMENT_STATUSES.find(
              (s) => s.value === emp.employmentStatus
            );
            return (
                  <tr key={emp._id || emp.email}>
                    <td>
                      <PersonCell
                        name={emp.name || emp.email}
                        subtitle={[emp.designation, emp.email].filter(Boolean).join(" · ")}
                      />
                    </td>
                    <td style={{ whiteSpace: "nowrap", color: "#5b6b6b" }}>{emp.employeeId || "-"}</td>
                    <td>{emp.department || "-"}</td>
                    <td>{emp.reportingManager ? managerName(emp.reportingManager) : "-"}</td>
                    <td>
                      {status ? (
                        <Badge colorScheme={status.color} borderRadius="full" px={2.5} fontSize="10px">
                          {status.label}
                        </Badge>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <Badge
                        colorScheme={emp.isOnboarded ? "green" : "orange"}
                        borderRadius="full"
                        px={2.5}
                        fontSize="10px"
                      >
                        {emp.isOnboarded ? "Onboarded" : "Pending"}
                      </Badge>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor="#327171"
                        color="#327171"
                        _hover={{ bg: "#327171", color: "white" }}
                        onClick={() => handleEdit(emp)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
            );
          })}
        </HrmsTable>
      </>

      {/* Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Edit Profile — {selectedEmployee?.name || selectedEmployee?.email}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Full Name</FormLabel>
                <Input
                  value={editData.name}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Designation</FormLabel>
                <Input
                  value={editData.designation}
                  onChange={(e) =>
                    setEditData({ ...editData, designation: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Date of Joining</FormLabel>
                <Input
                  type="date"
                  value={editData.dateOfJoining}
                  min={bounds.joining.min}
                  max={bounds.joining.max}
                  onChange={(e) =>
                    setEditData({ ...editData, dateOfJoining: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Birthday</FormLabel>
                <Input
                  type="date"
                  value={editData.birthday}
                  min={bounds.birthday.min}
                  max={bounds.birthday.max}
                  onChange={(e) =>
                    setEditData({ ...editData, birthday: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Address</FormLabel>
                <Input
                  value={editData.address}
                  onChange={(e) =>
                    setEditData({ ...editData, address: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Reporting Manager</FormLabel>
                <Select
                  placeholder="None"
                  value={editData.reportingManager}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      reportingManager: e.target.value,
                    })
                  }
                  focusBorderColor="teal.500"
                >
                  {hasLegacyManager && (
                    <option value={editData.reportingManager}>
                      {editData.reportingManager} (not an employee email)
                    </option>
                  )}
                  {managerOptions.map((e) => (
                    <option key={e.email} value={e.email}>
                      {e.name || e.email}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Employee ID</FormLabel>
                <Input
                  value={editData.employeeId}
                  onChange={(e) =>
                    setEditData({ ...editData, employeeId: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Department</FormLabel>
                <Input
                  value={editData.department}
                  onChange={(e) =>
                    setEditData({ ...editData, department: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Division</FormLabel>
                <Input
                  value={editData.division}
                  onChange={(e) =>
                    setEditData({ ...editData, division: e.target.value })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Employment Status</FormLabel>
                <Select
                  placeholder="Not set"
                  value={editData.employmentStatus}
                  onChange={(e) =>
                    setEditData({ ...editData, employmentStatus: e.target.value })
                  }
                  focusBorderColor="teal.500"
                >
                  {EMPLOYMENT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Promotions</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={editData.promotionCount}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      promotionCount: Number(e.target.value) || 0,
                    })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Leave Year Starts On</FormLabel>
                <Input
                  type="date"
                  value={editData.leaveCycleStartDate}
                  min={bounds.leaveCycle.min}
                  max={bounds.leaveCycle.max}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      leaveCycleStartDate: e.target.value,
                    })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Next Appraisal Due Date</FormLabel>
                <Input
                  type="date"
                  value={editData.nextAppraisalDueDate}
                  min={bounds.appraisal.min}
                  max={bounds.appraisal.max}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      nextAppraisalDueDate: e.target.value,
                    })
                  }
                  focusBorderColor="teal.500"
                />
              </FormControl>
            </SimpleGrid>

            <FormControl mt={4}>
              <FormLabel>Yearly Leave Quota (leave blank for the standard quota)</FormLabel>
              <SimpleGrid columns={3} spacing={4}>
                {QUOTA_TYPES.map(({ key, label }) => (
                  <Input
                    key={key}
                    type="number"
                    min={0}
                    placeholder={label}
                    aria-label={`${label} leave quota`}
                    value={editData.quota[key]}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        quota: { ...editData.quota, [key]: e.target.value },
                      })
                    }
                    focusBorderColor="teal.500"
                  />
                ))}
              </SimpleGrid>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Roles</FormLabel>
              <CheckboxGroup
                value={editData.roleIds}
                onChange={(values) =>
                  setEditData({ ...editData, roleIds: values as string[] })
                }
              >
                <Flex gap={4} wrap="wrap">
                  {roles.map((role) => (
                    <Checkbox key={role._id} value={role._id}>
                      {role.name}
                    </Checkbox>
                  ))}
                </Flex>
              </CheckboxGroup>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={handleSave}
              isLoading={isSaving}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
